import Card from '@app/components/card/card';
import CardContent from '@app/components/card/card-content';
import CardHeader from '@app/components/card/card-header';
import CheckBoxInput from '@app/components/form/input/check-box-input.tsx';
import { SpinnerSize } from '@app/components/spinner-size.ts';
import Spinner from '@app/components/spinner.tsx';
import { BannerPlatform, PlatformType } from '@app/graphql/types/graphql.ts';
import ApplicationErrorView from '@app/module/error/application-error-view.tsx';
import { formatGraphQlError, validationErrors } from '@app/module/error/error.ts';
import HolidayTimeOutBanner from '@app/page/appointment/appointment-create/component/holiday-time-out-banner.tsx';
import { useCompany } from '@app/page/menu-preview/use-menu-query.ts';
import NewsBanner from '@app/page/online-order/banner/news-banner/news-banner.tsx';
import DurationInput from '@app/page/table-reservation/component/duration-input.tsx';
import TableReservationDateInput from '@app/page/table-reservation/component/table-reservation-date-input.tsx';
import { analytics } from '@app/utils/analytics.ts';
import useFormData from '@app/utils/use-form-data.ts';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { captureException } from '@sentry/react';
import { useTolgee, useTranslate } from '@tolgee/react';
import classNames from 'classnames';
import { logEvent, setUserProperties } from 'firebase/analytics';
import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useNavigate } from 'react-router-dom';
import useCreateTableBooking from '../logic/use-create-table-booking.ts';
import TopBanner from '@app/page/online-order/banner/top-banner/top-banner.tsx';
import ReservationBusinessCard from './reservation-business-card.tsx';

interface TableBookingCreateForm {
  branchId: string;
  platformType: PlatformType;
  seats: number;
  guestName: string;
  message: string;
  startHour: string;
  startDate: string;
  phone: string;
  email: string;
  receiveNewsletter: boolean;
}

interface SelectedBranch {
  __typename?: 'StoreFront_Branch' | undefined;
  id: string;
  name: string;
  website?: string | null | undefined;
  streetAddress: string;
  addressLocality: string;
  postalCode: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  tableReservationSetting: {
    seatLimit: number;
    phone: string;
    status: boolean;
    flexDuration: number[];
    flexEnabled: boolean;
  };
  company: {
    logo?: string | null | undefined;
    settings: {
      customerDefaultLanguage: string;
      timezone: string;
    };
  };
}

const TableReservationCreateForm = (props: { branchID: string; companyID: string; selectedBranch: SelectedBranch }) => {
  const { selectedBranch, branchID, companyID } = props;
  const { data } = useCompany(companyID);
  const navigate = useNavigate();
  const [emptyError, setEmptyError] = useState<Record<string, boolean>>({});
  const {
    data: form,
    handle,
    manualHandle,
  } = useFormData<TableBookingCreateForm>({
    branchId: branchID,
    platformType: PlatformType.System,
    seats: 1,
    guestName: '',
    message: '',
    startHour: '',
    startDate: '',
    phone: '',
    email: '',
    receiveNewsletter: false,
  });
  const [create, { error, loading: mutating }] = useCreateTableBooking();

  const applicationErrors = formatGraphQlError(error?.graphQLErrors);
  const validationError = validationErrors(applicationErrors);

  const [flexDuration, setFlexDuration] = useState<number | null>(null);

  useEffect(() => {
    document.title = `Reservation at ${selectedBranch.name}`;
    localStorage.setItem('language', selectedBranch.company.settings.customerDefaultLanguage);
  }, [selectedBranch]);

  useEffect(() => {
    if (companyID && branchID) {
      setUserProperties(analytics, { companyId: companyID, branchId: branchID });
    }

    logEvent(analytics, 'table_reservation_start', {
      companyID: companyID,
      branchID: branchID,
    });
  }, [branchID, companyID]);

  const validate = () => {
    let flag = true;
    setEmptyError({});
    if (mutating) {
      flag = false;
    }
    if (form.branchId === '') {
      flag = false;
    }
    if (form.guestName === '') {
      setEmptyError((previousEmptyError) => ({
        ...previousEmptyError,
        guestName: true,
      }));
      flag = false;
    }
    if (form.email === '') {
      setEmptyError((previousEmptyError) => ({
        ...previousEmptyError,
        email: true,
      }));
      flag = false;
    }
    if (form.phone === '') {
      setEmptyError((previousEmptyError) => ({
        ...previousEmptyError,
        phone: true,
      }));
      flag = false;
    }
    if (isNaN(form.seats)) {
      flag = false;
    }
    if (form.startDate === '') {
      setEmptyError((previousEmptyError) => ({
        ...previousEmptyError,
        startDate: true,
      }));
      flag = false;
    }

    if (form.startHour === '') {
      setEmptyError((previousEmptyError) => ({
        ...previousEmptyError,
        startHour: true,
      }));
      flag = false;
    }

    return flag;
  };

  const analyticBeginEdit = (fieldName: string) => {
    logEvent(analytics, 'begin_edit_' + fieldName, {
      companyID: companyID,
      branchID: branchID,
    });
  };

  const analyticEndEdit = (fieldName: string) => {
    logEvent(analytics, 'end_edit_' + fieldName, {
      companyID: companyID,
      branchID: branchID,
    });
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    create({
      variables: {
        branchId: form.branchId,
        input: {
          customerLanguage: localStorage.getItem('language') ?? 'de',
          platformType: form.platformType,
          seats: +form.seats,
          guestName: form.guestName,
          message: form.message,
          startTime: `${form.startDate}T${form.startHour}`,
          flexDuration: flexDuration === 0 ? null : flexDuration,
          phone: form.phone,
          email: form.email,
          receiveNewsletterPerEmail: form.receiveNewsletter,
        },
      },
    })
      .then((value) => {
        logEvent(analytics, 'table_reservation_end', {
          companyID: companyID,
          branchID: branchID,
        });

        navigate(
          '/table-reservation/track?tableReservationId=' +
            (value.data?.storefront_createTableReservation.id ?? '') +
            '&branchId=' +
            'default'
        );
      })
      .catch((e) => {
        logEvent(analytics, 'table_reservation_failure_create', {
          companyID: companyID,
          branchID: branchID,
          ...validationError,
        });

        captureException(e);
      });
  };

  const handleSeats = (value: number) => {
    let result = value;
    if (value < 1) {
      result = 1;
    }
    if (value > selectedBranch.tableReservationSetting.seatLimit) {
      result = selectedBranch.tableReservationSetting.seatLimit;
    }
    manualHandle('seats', result);
  };

  const { t } = useTranslate();

  return (
    <>
      <div className="">
        <TopBanner branchID={branchID} platform={BannerPlatform.TableReservation} />
        <HolidayTimeOutBanner branchId={branchID} timezone={selectedBranch.company.settings.timezone} />
      </div>

      <ReservationBusinessCard branchID={branchID} />

      <div className="mt-8 flex w-full flex-col items-center sm:justify-center">
        <div className="flex w-full max-w-5xl flex-col space-y-4">
          <NewsBanner branchID={branchID} platform={BannerPlatform.TableReservation} />
          <Card className="shadow-base border">
            <CardHeader title={t('reservation.header', 'Table reservation')}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <LanguageSelector />
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {/* Form Content */}
              {form.branchId !== '' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Guest name */}
                    <div className="space-y-2">
                      <label htmlFor="guestName" className="block text-sm font-medium text-gray-900">
                        {t('reservation.create-form.form-input.guest-name.label', 'Guest Name')}
                      </label>
                      <input
                        id="guestName"
                        name="guestName"
                        type="guestName"
                        value={form.guestName}
                        onChange={handle}
                        onFocus={(e) => {
                          analyticBeginEdit(e.target.name);
                        }}
                        onBlur={(e) => {
                          analyticEndEdit(e.target.name);
                        }}
                        autoComplete="guestName"
                        className="block w-full rounded-md border-0 p-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                      {validationError.guestName && (
                        <p className="mt-2 text-sm text-red-600">{validationError.guestName}</p>
                      )}
                      {emptyError.guestName && (
                        <p className="mt-2 text-sm text-red-600">
                          {t('reservation.create-form.form-input.empty-error.guest-name', 'Guest name is required')}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                        {t('reservation.create-form.form-input.email.label', 'Email')}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handle}
                        onFocus={(e) => {
                          analyticBeginEdit(e.target.name);
                        }}
                        onBlur={(e) => {
                          analyticEndEdit(e.target.name);
                        }}
                        autoComplete="email"
                        className="block w-full rounded-md border-0 p-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                      {validationError.email && <p className="mt-2 text-sm text-red-600">{validationError.email}</p>}
                      {emptyError.email && (
                        <p className="mt-2 text-sm text-red-600">
                          {t('reservation.create-form.form-input.empty-error.email', 'Email is required')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone and Seats in the same row */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Phone */}
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-900">
                        {t('reservation.create-form.form-input.phone.label', 'Phone')}
                      </label>
                      <div className="flex">
                        <PhoneInput
                          className="w-full rounded-md border-0 p-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          defaultCountry={'DE'}
                          placeholder={t('reservation.create-form.form-input.phone.placeholder', 'Enter phone number')}
                          value={form.phone}
                          onChange={(value) => {
                            manualHandle('phone', value?.toString() ?? '');
                          }}
                          onFocus={() => {
                            analyticBeginEdit('phone');
                          }}
                          onBlur={() => {
                            analyticEndEdit('phone');
                          }}
                        />
                      </div>
                      {validationError.phone && <p className="mt-2 text-sm text-red-600">{validationError.phone}</p>}
                      {emptyError.phone && (
                        <p className="mt-2 text-sm text-red-600">
                          {t('reservation.create-form.form-input.empty-error.phone', 'Phone is required')}
                        </p>
                      )}
                    </div>

                    {/* Seats */}
                    <div className="space-y-2">
                      <label htmlFor="seats" className="block text-sm font-medium text-gray-900">
                        {t('reservation.create-form.form-input.seats.label', 'Number of guests')}{' '}
                        <span className="pl-1 font-normal italic text-gray-400">
                          {'( '}
                          {t(
                            'reservation.create-form.form-input.seats.description',
                            'Get contact to us if your group has more than '
                          )}
                          {selectedBranch.tableReservationSetting.seatLimit}
                          {' )'}
                        </span>
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            handleSeats(form.seats - 1);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                        >
                          -
                        </button>
                        <input
                          className="block w-16 rounded-md border-0 p-2 py-1.5 text-center text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          type="number"
                          id="seats"
                          name="seats"
                          value={form.seats}
                          onChange={() => undefined}
                          min="1"
                          max={selectedBranch.tableReservationSetting.seatLimit}
                          readOnly
                        />
                        <button
                          onClick={() => {
                            handleSeats(form.seats + 1);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                        >
                          +
                        </button>
                      </div>
                      {validationError.seats && <p className="mt-2 text-sm text-red-600">{validationError.seats}</p>}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-900">
                      {t('reservation.create-form.form-input.message.label', 'Message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={3}
                      value={form.message}
                      onChange={(e) => {
                        manualHandle('message', e.target.value);
                      }}
                      onFocus={(e) => {
                        analyticBeginEdit(e.target.name);
                      }}
                      onBlur={(e) => {
                        analyticEndEdit(e.target.name);
                      }}
                      className="block w-full rounded-md border-0 p-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    {validationError.message && <p className="mt-2 text-sm text-red-600">{validationError.message}</p>}
                  </div>

                  {/* Flex Duration */}
                  {selectedBranch.tableReservationSetting.flexEnabled && (
                    <div className="space-y-2">
                      <DurationInput
                        items={selectedBranch.tableReservationSetting.flexDuration}
                        value={flexDuration}
                        onChange={(value) => {
                          setFlexDuration(value);
                          logEvent(analytics, 'table_reservation_flex_duration', {
                            companyID: companyID,
                            branchID: branchID,
                            selected: value,
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* Booking time */}
                  <div className="space-y-4">
                    <TableReservationDateInput
                      currentDateValue={form.startDate}
                      currentTimeValue={form.startHour}
                      handleDate={(value) => {
                        manualHandle('startDate', value);
                      }}
                      handleTime={(value) => {
                        manualHandle('startHour', value);
                      }}
                      branchId={form.branchId}
                      timezone={selectedBranch.company.settings.timezone}
                    />
                    {validationError.startDate && (
                      <p className="mt-2 text-sm text-red-600">{validationError.startDate}</p>
                    )}
                    {emptyError.startHour && (
                      <p className="mt-2 text-sm text-red-600">
                        {t('reservation.create-form.form-input.empty-error.date', 'You must select a hour')}
                      </p>
                    )}
                  </div>

                  {/* Newsletter */}
                  <div className="flex items-center space-x-2">
                    <CheckBoxInput
                      onChange={(_, value) => {
                        manualHandle('receiveNewsletter', value);
                      }}
                      value={form.receiveNewsletter}
                      name={t(
                        'storefront-appointment.create.contact-info.button.receive-new-letter',
                        'I want to receive exclusive offers via emails from {company}',
                        {
                          company: data?.storefront_company.name,
                        }
                      )}
                    />
                  </div>

                  <ApplicationErrorView error={error} />

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-x-6">
                    {selectedBranch.website && selectedBranch.website.length > 0 && (
                      <button
                        onClick={() => {
                          if (selectedBranch.website) {
                            window.location.href = selectedBranch.website;
                            logEvent(analytics, 'table_reservation_return_to_website', {
                              companyID: companyID,
                              branchID: branchID,
                            });
                          }
                        }}
                        type="button"
                        className="text-sm font-semibold text-gray-900"
                      >
                        {t('reservation.button.return-to-website', 'Return to website')}
                      </button>
                    )}
                    <button
                      type="submit"
                      className={classNames(
                        'flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-gray-400 md:w-32',
                        {}
                      )}
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {mutating && (
                          <div className="h-4 w-4">
                            <Spinner size={SpinnerSize.Small} />
                          </div>
                        )}
                        {t('reservation.button.reserve', 'Reserve')}
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default TableReservationCreateForm;

export function LanguageSelector(props: { customerDefaultLanguage?: string }) {
  const [selected, setSelected] = useState<Language>(languages[0]);
  const { changeLanguage } = useTolgee(['language']);

  useEffect(() => {
    if (!props.customerDefaultLanguage) {
      const currentLanguage = localStorage.getItem('language') ?? 'de';
      setSelected(languages.find((language) => language.languageCode == currentLanguage) ?? languages[0]);
      changeLanguage(currentLanguage).catch(captureException);
      return;
    }

    localStorage.setItem('language', props.customerDefaultLanguage);
    setSelected(languages.find((language) => language.languageCode == props.customerDefaultLanguage) ?? languages[0]);
    changeLanguage(props.customerDefaultLanguage).catch(captureException);
  }, [changeLanguage, props.customerDefaultLanguage]);

  const handleChange = (selectedLanguage: Language) => {
    setSelected(selectedLanguage);
    changeLanguageCallback(selectedLanguage.languageCode);
  };

  const changeLanguageCallback = (lng: string) => {
    changeLanguage(lng).catch(captureException);
    localStorage.setItem('language', lng);
  };

  return (
    <Listbox value={selected} onChange={handleChange}>
      <div className="relative mt-2">
        <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
          <span className="block truncate">
            <img
              src={`https://flagcdn.com/16x12/${selected.country}.png`}
              width="20"
              height="16"
              alt={selected.country}
            />
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
          </span>
        </ListboxButton>

        <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {languages.map((lang) => (
            <ListboxOption
              key={lang.id}
              value={lang}
              className="relative m-1 cursor-default select-none py-2 pl-8 pr-4 text-gray-900 hover:bg-indigo-600 hover:text-white"
            >
              <span className="absolute inset-y-0 left-0 m-1 flex items-center pl-2">
                <img src={`https://flagcdn.com/16x12/${lang.country}.png`} width="20" height="16" alt={lang.country} />
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
interface Language {
  id: number;
  language: string;
  languageCode: string;
  country: string;
}

const languages: Language[] = [
  { id: 1, language: 'English', languageCode: 'en', country: 'us' },
  { id: 2, language: 'Germany', languageCode: 'de', country: 'de' },
  { id: 3, language: 'Vietnamese', languageCode: 'vi', country: 'vn' },
  { id: 4, language: 'Polish', languageCode: 'pl', country: 'pl' },
];
